import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppApi } from '@/lib/api';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

const HIDDEN = ['password', 'token_hash'];
/** Numeric/opaque columns are shown but excluded from search, so a query like "7" can't
 *  match a company_id the user never meant to search. */
const UNSEARCHABLE = ['id', 'company_id', 'branch_id', 'created_by'];

/**
 * Generic READ-ONLY list page for a model — responsive by design (mobile cards < 768px).
 * Customize per model by replacing this with a dedicated page when the app needs it.
 *
 * Deliberately has no "New" button. It used to render one gated on `action="create"`, a
 * verb that exists in no module in services/rbac/modules.json, so it was invisible to
 * every non-superadmin — and for a superadmin it rendered with no onClick at all. There
 * is also nowhere generic for it to go: creating a record needs the model's own field
 * shape and validation, which is exactly what a bespoke page provides. Add a
 * CUSTOM_PAGES entry in client/src/routes.jsx when a model needs create.
 */
export default function CrudPage({ model, title }) {
  const { fetchApi } = useAppApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    setErr('');
    return fetchApi('crud', { model, action: 'list', limit: 50 })
      .then((res) => setRows(Array.isArray(res) ? res : (res && res.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, [model]);

  useEffect(() => { reload(); }, [reload]);

  const columns = useMemo(() => (rows[0]
    ? Object.keys(rows[0])
      .filter((k) => !HIDDEN.includes(k))
      .map((k) => ({ key: k, label: k, searchable: !UNSEARCHABLE.includes(k) }))
    : []), [rows]);

  return (
    <PageShell className="space-y-3">
      <PageHeader title={title || model} />
      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        error={err}
        emptyText={`No ${title || model} rows yet.`}
        search
        onReload={reload}
      />
    </PageShell>
  );
}
